// Quality API
import api, { type AxiosResponse, type PaginatedResponse } from './client';
import type {
  QualityMetric,
  NonConformanceReport,
  NCRDocument,
  NCRComment,
  NCRActionLog,
  NCRTechnicalReview,
  NCRCollectionProcess,
  NCRCreditProcess,
  NCRReplacementProcess,
  InspectionRecord,
  QualityDashboardStats,
  QualityDashboardSummary,
  InspectionPassRate,
  Concession,
  ConcessionDocument,
  ConcessionMentionOptions,
  ConcessionComment,
  ConcessionActionLog,
  ConcessionDashboardStats,
  SourceOfRejection,
  CommonisedCorrectiveAction,
  FaultCodeGroup,
  FaultCodeCategory,
  ApprovalGroup,
  Accreditation,
  SupplierAccreditation,
  ApprovalRegisterMatrix,
  QueryParams,
} from './types';

// CR-5887 (TEMPORARY — remove with the test trigger): response shape of the
// admin-only daily Quality summary "send now" endpoint.
export interface DailyQualitySummaryTriggerResult {
  status: string;
  mode: 'single' | 'all';
  to?: string;
  result: {
    sent?: number;
    role?: string | null;
    reason?: string | null;
    emails_sent?: number;
    emails_skipped?: number;
    customers_processed?: number;
    suppliers_processed?: number;
  };
}

export const qualityAPI = {
  // Metrics
  getMetrics: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: QualityMetric[] }>> =>
    api.get('/quality/metrics/', { params }),

  getDashboardSummary: (): Promise<AxiosResponse<QualityDashboardSummary>> =>
    api.get('/quality/metrics/dashboard_summary/'),

  // NCRs
  getNCRs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<NonConformanceReport>>> =>
    api.get('/quality/ncrs/', { params }),

  /**
   * Batched quick-filter chip counts: one request replaying each chip's exact
   * list params through the NCR list queryset (mirrors purchasing/lines/tab-counts/).
   */
  getNCRTabCounts: (tabs: Record<string, Record<string, string | number | boolean>>): Promise<AxiosResponse<{ counts: Record<string, number | null> }>> =>
    api.post('/quality/ncrs/tab-counts/', { tabs }),

  getNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.get(`/quality/ncrs/${id}/`),

  createNCR: (data: Partial<NonConformanceReport>): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post('/quality/ncrs/', data),

  updateNCR: (id: number, data: Partial<NonConformanceReport>): Promise<AxiosResponse<NonConformanceReport>> =>
    api.patch(`/quality/ncrs/${id}/`, data),

  /**
   * Delete an NCR. Customer admins only, and only NCRs raised by hand in the
   * portal — the API rejects imported ones, which the ERP still owns.
   */
  deleteNCR: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/ncrs/${id}/`),

  /**
   * Hide an NCR from the working lists without deleting it. Customer admins
   * only; reversible via {@link unarchiveNCR}; metrics are unaffected.
   */
  archiveNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/archive/`),

  /** Restore an archived NCR to the working lists. Customer admins only. */
  unarchiveNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/unarchive/`),

  // CR-3396: Accept view context params for hierarchical filtering
  getNCRStats: (params?: Record<string, string | number>): Promise<AxiosResponse<QualityDashboardStats>> =>
    api.get('/quality/ncrs/dashboard_stats/', { params }),

  // scores: optional RC/CA scores (1-5) the customer grades the CAR with
  // when approving — captured atomically with the decision.
  closeNCR: (id: number, scores?: { rc_score?: number; ca_score?: number }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/close_ncr/`, scores ?? {}),

  // Persist the RC/CA grades on their own (no approve/reject decision), so the
  // register column + 8D Score (= RC*CA) update as soon as the customer scores
  // the CAR — even if Approve is still gated by collection/credit workflows.
  setCarScores: (id: number, scores: { rc_score?: number; ca_score?: number }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/set-car-scores/`, scores),

  // CR-6024: customer rejects a submitted CAR and sends it back for rework.
  // NCR transitions car_submitted -> ca_submitted; supplier sees the reason
  // on the Corrective Action tab and can amend then resubmit. Accepts the
  // same optional RC/CA scores as closeNCR.
  rejectCAR: (id: number, rejectionReason: string, scores?: { rc_score?: number; ca_score?: number }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reject_car/`, { rejection_reason: rejectionReason, ...(scores ?? {}) }),

  // NCR Workflow Actions
  submitDetail: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_detail/`),

  submitRCA: (id: number, data: {
    why_1: string;
    why_2: string;
    why_3: string;
    why_4: string;
    why_5: string;
    selected_root_cause: string;
    immediate_corrective_action: string;
    disposition_instructions?: string;
  }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_rca/`, data),

  submitCAR: (id: number, data: {
    corrective_action: string;
    preventive_action: string;
    impact_assessment: string;
    // 8D: Impact on Similar Products
    impact_on_similar_products?: string;
    similar_products_affected?: boolean;
    // 8D: Training
    training_required: boolean;
    training_details?: string;
    // 8D: Documentation Updates
    doc_process_flow_updated?: boolean;
    doc_control_plan_updated?: boolean;
    doc_fmea_updated?: boolean;
    doc_work_instructions_updated?: boolean;
    doc_other_updated?: string;
    documentation_references: string;
    corrective_action_notes: string;
    // Customer review 2026-07: 8D extensions
    commonised_corrective_action?: number | null;
    horizontal_deployment_parts?: string;
  }): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/submit_car/`, data),

  // Customer review 2026-07: live repeat-issue lookup for the create form
  ncrRepeatCheck: (params: { part_number: string; fault_group: number }): Promise<AxiosResponse<{ is_repeat: boolean; previous_count: number; last_ncr_id: number | null; last_ncr_number: string | null; last_ncr_date: string | null }>> =>
    api.get('/quality/ncrs/repeat-check/', { params }),

  // Customer review 2026-07: current stock snapshot for the containment block
  ncrPartStock: (params: { part_number: string }): Promise<AxiosResponse<{ stock_quantity: number | null; warehouses: Array<{ warehouse_code: string; stock_quantity: string }> }>> =>
    api.get('/quality/ncrs/part-stock/', { params }),

  rejectNCR: (id: number, rejection_reason: string): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reject_ncr/`, { rejection_reason }),

  approveRejection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/approve_rejection/`),

  declineRejection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/decline_rejection/`),

  reopenNCR: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/reopen_ncr/`),

  confirmCollection: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/confirm-collection/`),

  confirmCredit: (id: number): Promise<AxiosResponse<NonConformanceReport>> =>
    api.post(`/quality/ncrs/${id}/confirm-credit/`),

  // Collection Process - Multi-step workflow
  getCollectionProcess: (ncrId: number): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.get(`/quality/ncrs/${ncrId}/collection/`),

  initiateCollection: (ncrId: number, data: {
    proposed_collection_date?: string;
    collection_address?: string;
    collection_notes?: string;
    customer_notes?: string;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/initiate/`, data),

  updateCollectionStatus: (ncrId: number, data: {
    status: string;
    collection_notes?: string;
    confirmed_collection_date?: string;
    tracking_reference?: string;
    actual_collection_date?: string;
    supplier_receipt_notes?: string;
  }): Promise<AxiosResponse<NCRCollectionProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/collection/update-status/`, data),

  // Credit Process - Multi-step workflow
  getCreditProcess: (ncrId: number): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.get(`/quality/ncrs/${ncrId}/credit/`),

  initiateCredit: (ncrId: number, data: {
    credit_amount?: number;
    credit_currency?: string;
    credit_reason?: string;
    customer_notes?: string;
  }): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/initiate/`, data),

  // Supplier submits/re-submits the credit note (file + number) — multipart.
  submitCreditNote: (ncrId: number, data: FormData): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/submit-note/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Customer approves or rejects the submitted credit note.
  updateCreditStatus: (ncrId: number, data: {
    status: string;
    credit_amount_confirmed?: number;
    customer_credit_reference?: string;
    approval_notes?: string;
  }): Promise<AxiosResponse<NCRCreditProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/credit/update-status/`, data),

  // Replacement Process - Multi-step workflow (mirrors Credit)
  initiateReplacement: (ncrId: number, data: {
    replacement_qty?: number;
    replacement_reason?: string;
    customer_notes?: string;
  }): Promise<AxiosResponse<NCRReplacementProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/replacement/initiate/`, data),

  // Supplier submits/re-submits the despatch details (reference + optional doc) — multipart.
  submitReplacementDispatch: (ncrId: number, data: FormData): Promise<AxiosResponse<NCRReplacementProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/replacement/submit-dispatch/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Customer confirms receipt of, or rejects, the despatched replacement.
  updateReplacementStatus: (ncrId: number, data: {
    status: string;
    customer_replacement_reference?: string;
    approval_notes?: string;
  }): Promise<AxiosResponse<NCRReplacementProcess>> =>
    api.post(`/quality/ncrs/${ncrId}/replacement/update-status/`, data),

  // NCR Supporting Resources
  getNCRDocuments: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRDocument[] }>> =>
    api.get('/quality/ncr-documents/', { params: { ncr: ncrId } }),

  uploadNCRDocument: (data: FormData): Promise<AxiosResponse<NCRDocument>> =>
    api.post('/quality/ncr-documents/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  deleteNCRDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/ncr-documents/${id}/`),

  getNCRComments: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRComment[] }>> =>
    api.get('/quality/ncr-comments/', { params: { ncr: ncrId } }),

  addNCRComment: (ncrId: number, comment: string, is_internal: boolean): Promise<AxiosResponse<NCRComment>> =>
    api.post('/quality/ncr-comments/', { ncr: ncrId, comment, is_internal }),

  getNCRActionLogs: (ncrId: number): Promise<AxiosResponse<{ count: number; results: NCRActionLog[] }>> =>
    api.get('/quality/ncr-logs/', { params: { ncr: ncrId } }),

  getNCRTechnicalReview: (ncrId: number): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.get('/quality/ncr-technical-reviews/', { params: { ncr: ncrId } }),

  createTechnicalReview: (ncrId: number): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post('/quality/ncr-technical-reviews/', { ncr: ncrId }),

  completeTechnicalReview: (id: number, review_notes: string, review_outcome: string): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post(`/quality/ncr-technical-reviews/${id}/complete_review/`, { review_notes, review_outcome }),

  rejectTechnicalReview: (id: number, rejection_reason: string): Promise<AxiosResponse<NCRTechnicalReview>> =>
    api.post(`/quality/ncr-technical-reviews/${id}/reject_review/`, { rejection_reason }),

  // Inspections
  getInspections: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: InspectionRecord[] }>> =>
    api.get('/quality/inspections/', { params }),

  getPassRate: (): Promise<AxiosResponse<InspectionPassRate>> =>
    api.get('/quality/inspections/pass_rate/'),

  // Concessions (CR-3470)
  getConcessions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Concession[] }>> =>
    api.get('/quality/concessions/', { params }),

  getConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.get(`/quality/concessions/${id}/`),

  createConcession: (data: Partial<Concession>): Promise<AxiosResponse<Concession>> =>
    api.post('/quality/concessions/', data),

  updateConcession: (id: number, data: Partial<Concession>): Promise<AxiosResponse<Concession>> =>
    api.patch(`/quality/concessions/${id}/`, data),

  deleteConcession: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/concessions/${id}/`),

  getConcessionStats: (params?: Record<string, string | number>): Promise<AxiosResponse<ConcessionDashboardStats>> =>
    api.get('/quality/concessions/dashboard_stats/', { params }),

  // Concession Workflow Actions
  submitConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/submit/`),

  markConcessionUnderReview: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/mark_under_review/`),

  approveConcession: (id: number, data: {
    review_notes?: string;
    quantity_approved?: number;
    valid_until?: string;
    // CR-6510: the legacy "Approval Process" block.
    qn_mrb_reference?: string;
    approver_name?: string;
    approver_position?: string;
    approver_department?: string;
    customer_response_date?: string;
  }): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/approve/`, data),

  rejectConcession: (id: number, rejection_reason: string): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/reject/`, { rejection_reason }),

  closeConcession: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/close/`),

  acceptConcessionRisk: (id: number): Promise<AxiosResponse<Concession>> =>
    api.post(`/quality/concessions/${id}/accept_risk/`),

  // Concession Supporting Resources
  getConcessionDocuments: (concessionId: number): Promise<AxiosResponse<ConcessionDocument[]>> =>
    api.get(`/quality/concessions/${concessionId}/documents/`),

  uploadConcessionDocument: (data: FormData): Promise<AxiosResponse<ConcessionDocument>> =>
    api.post('/quality/concession-documents/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),

  deleteConcessionDocument: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/concession-documents/${id}/`),

  // CR-6510: colleagues and departments this user may ask to look at a
  // comment. Scoped server-side to the caller's own company.
  getConcessionMentionOptions: (
    concessionId: number,
  ): Promise<AxiosResponse<ConcessionMentionOptions>> =>
    api.get(`/quality/concessions/${concessionId}/mention-options/`),

  getConcessionComments: (concessionId: number): Promise<AxiosResponse<ConcessionComment[]>> =>
    api.get(`/quality/concessions/${concessionId}/comments/`),

  addConcessionComment: (
    concessionId: number,
    comment: string,
    is_internal: boolean,
    // CR-6510: colleagues / departments asked to look. Anything outside the
    // caller's own company is dropped server-side.
    mentions?: { mentioned_user_ids?: number[]; mentioned_departments?: string[] },
  ): Promise<AxiosResponse<ConcessionComment>> =>
    api.post(`/quality/concessions/${concessionId}/add_comment/`, {
      comment,
      is_internal,
      ...(mentions?.mentioned_user_ids?.length
        ? { mentioned_user_ids: mentions.mentioned_user_ids } : {}),
      ...(mentions?.mentioned_departments?.length
        ? { mentioned_departments: mentions.mentioned_departments } : {}),
    }),

  getConcessionActionLog: (concessionId: number): Promise<AxiosResponse<ConcessionActionLog[]>> =>
    api.get(`/quality/concessions/${concessionId}/action_log/`),

  // Quality Config (admin-managed dropdown options)
  getSourcesOfRejection: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SourceOfRejection[] }>> =>
    api.get('/quality/sources-of-rejection/', { params }),

  createSourceOfRejection: (data: Partial<SourceOfRejection>): Promise<AxiosResponse<SourceOfRejection>> =>
    api.post('/quality/sources-of-rejection/', data),

  updateSourceOfRejection: (id: number, data: Partial<SourceOfRejection>): Promise<AxiosResponse<SourceOfRejection>> =>
    api.patch(`/quality/sources-of-rejection/${id}/`, data),

  deleteSourceOfRejection: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/sources-of-rejection/${id}/`),

  // Customer review 2026-07: commonised corrective actions master list
  getCommonisedCorrectiveActions: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CommonisedCorrectiveAction[] }>> =>
    api.get('/quality/commonised-corrective-actions/', { params }),

  createCommonisedCorrectiveAction: (data: Partial<CommonisedCorrectiveAction>): Promise<AxiosResponse<CommonisedCorrectiveAction>> =>
    api.post('/quality/commonised-corrective-actions/', data),

  updateCommonisedCorrectiveAction: (id: number, data: Partial<CommonisedCorrectiveAction>): Promise<AxiosResponse<CommonisedCorrectiveAction>> =>
    api.patch(`/quality/commonised-corrective-actions/${id}/`, data),

  deleteCommonisedCorrectiveAction: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/commonised-corrective-actions/${id}/`),

  getFaultGroups: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: FaultCodeGroup[] }>> =>
    api.get('/quality/fault-groups/', { params }),

  createFaultGroup: (data: Partial<FaultCodeGroup>): Promise<AxiosResponse<FaultCodeGroup>> =>
    api.post('/quality/fault-groups/', data),

  updateFaultGroup: (id: number, data: Partial<FaultCodeGroup>): Promise<AxiosResponse<FaultCodeGroup>> =>
    api.patch(`/quality/fault-groups/${id}/`, data),

  deleteFaultGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-groups/${id}/`),

  getFaultCategories: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: FaultCodeCategory[] }>> =>
    api.get('/quality/fault-categories/', { params }),

  createFaultCategory: (data: Partial<FaultCodeCategory>): Promise<AxiosResponse<FaultCodeCategory>> =>
    api.post('/quality/fault-categories/', data),

  updateFaultCategory: (id: number, data: Partial<FaultCodeCategory>): Promise<AxiosResponse<FaultCodeCategory>> =>
    api.patch(`/quality/fault-categories/${id}/`, data),

  deleteFaultCategory: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-categories/${id}/`),

  // CR-5333: Approval Groups + Accreditations.
  getApprovalGroups: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: ApprovalGroup[] }>> =>
    api.get('/quality/approval-groups/', { params }),

  createApprovalGroup: (data: Partial<ApprovalGroup>): Promise<AxiosResponse<ApprovalGroup>> =>
    api.post('/quality/approval-groups/', data),

  updateApprovalGroup: (id: number, data: Partial<ApprovalGroup>): Promise<AxiosResponse<ApprovalGroup>> =>
    api.patch(`/quality/approval-groups/${id}/`, data),

  deleteApprovalGroup: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/approval-groups/${id}/`),

  getAccreditations: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: Accreditation[] }>> =>
    api.get('/quality/accreditations/', { params }),

  createAccreditation: (data: Partial<Accreditation>): Promise<AxiosResponse<Accreditation>> =>
    api.post('/quality/accreditations/', data),

  updateAccreditation: (id: number, data: Partial<Accreditation>): Promise<AxiosResponse<Accreditation>> =>
    api.patch(`/quality/accreditations/${id}/`, data),

  // CR-5534: Upload (or replace) the accreditation's logo. Separate from
  // updateAccreditation because file uploads require multipart/form-data.
  uploadAccreditationLogo: (id: number, file: File): Promise<AxiosResponse<Accreditation>> => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.patch(`/quality/accreditations/${id}/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAccreditation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/accreditations/${id}/`),

  // CR-5534: Quality Approval Register (Suppliers × Accreditations matrix +
  // CRUD on the supplier-accreditation bind table). The matrix endpoint
  // composes the full grid in one round trip; the CRUD endpoints back
  // the add/edit/delete dialog on each cell. The optional view params
  // (site / company / group) come from useView().getViewQueryParams()
  // and scope the matrix to the currently-picked customer in the top-bar.
  getApprovalRegister: (params?: Record<string, number | undefined>): Promise<AxiosResponse<ApprovalRegisterMatrix>> =>
    api.get('/quality/approval-register/', { params }),

  getSupplierAccreditations: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SupplierAccreditation[] }>> =>
    api.get('/quality/supplier-accreditations/', { params }),

  getSupplierAccreditation: (id: number): Promise<AxiosResponse<SupplierAccreditation>> =>
    api.get(`/quality/supplier-accreditations/${id}/`),

  createSupplierAccreditation: (data: FormData): Promise<AxiosResponse<SupplierAccreditation>> =>
    api.post('/quality/supplier-accreditations/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateSupplierAccreditation: (id: number, data: FormData): Promise<AxiosResponse<SupplierAccreditation>> =>
    api.patch(`/quality/supplier-accreditations/${id}/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteSupplierAccreditation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/supplier-accreditations/${id}/`),

  // CR-5887 (TEMPORARY — remove after testing): fire the Daily Quality
  // Summary email immediately instead of waiting for the 07:00 UTC beat.
  // Pass `to` to send only to that address; omit to send to all recipients.
  triggerDailyQualitySummary: (
    to?: string,
  ): Promise<AxiosResponse<DailyQualitySummaryTriggerResult>> =>
    api.post('/quality/daily-summary/send-now/', to ? { to } : {}),
};
